import { get, isEmpty, isNil } from "lodash";
import { useMemo } from "react";
import {
  MdOutlineCalculate,
  MdOutlineGroups,
  MdOutlineMedicalServices,
  MdWarningAmber,
} from "react-icons/md";
import format from "string-template";
import Input from "shared/components/Input";
import Loading from "shared/components/Spin";
import Select from "shared/components/Select";
import { PRIMARY_MEMBER_TYPES } from "utils/constants";
import InfoBox from "../Components/InfoBox";
import Section from "../Components/Section";
import {
  COLLECTIVE_REGIME_ORDER,
  COMMERCIAL_ADJUSTMENTS,
  CONTRIBUTION_STRUCTURE_ORDER,
  NIL_PLACEHOLDER,
} from "../constants";
import {
  compositionErrorMessage,
  formatCurrency,
  formatRate,
  summaryByCombination,
} from "../helpers";

const Simulator = ({
  translation: T,
  structure,
  regime,
  headcounts,
  allocationErrors,
  commercialAdjustment,
  cost,
  costLoading,
  costRefreshing,
  costFailed,
  onStructureChange,
  onRegimeChange,
  onHeadcountChange,
  onCommercialAdjustmentChange,
  disabled,
}) => {
  const costDetails = get(cost, "costDetails");

  const primaryMembers = useMemo(
    () => get(PRIMARY_MEMBER_TYPES, structure, []),
    [structure],
  );

  const structureOptions = useMemo(
    () =>
      CONTRIBUTION_STRUCTURE_ORDER.map((value) => ({
        value,
        label: T.STRUCTURES[value],
      })),
    [T],
  );

  const regimeOptions = useMemo(
    () =>
      COLLECTIVE_REGIME_ORDER.map((value) => ({
        value,
        label: T.REGIMES[value],
      })),
    [T],
  );

  const adjustmentOptions = useMemo(
    () =>
      COMMERCIAL_ADJUSTMENTS.map((value) => ({
        value,
        label: format(T.OFFER.SIMULATOR.ADJUSTMENT.OPTION, {
          value: value > 0 ? `+${value}` : value,
        }),
      })),
    [T],
  );

  const summaries = useMemo(
    () => summaryByCombination(costDetails),
    [costDetails],
  );

  return (
    <Section
      title={T.OFFER.SIMULATOR.TITLE}
      description={T.OFFER.SIMULATOR.DESCRIPTION}
    >
      <div className="ac-collectif__simulator" aria-busy={costRefreshing}>
        <Select
          disabled={disabled}
          options={structureOptions}
          icon={<MdOutlineGroups size={22} />}
          label={T.OFFER.SIMULATOR.STRUCTURE.LABEL}
          placeholder={T.OFFER.SIMULATOR.STRUCTURE.PLACEHOLDER}
          value={structure}
          onChange={onStructureChange}
          name="contributionStructure"
        />
        <Select
          disabled={disabled}
          options={regimeOptions}
          icon={<MdOutlineMedicalServices size={22} />}
          label={T.OFFER.SIMULATOR.REGIME.LABEL}
          placeholder={T.OFFER.SIMULATOR.REGIME.PLACEHOLDER}
          value={regime}
          onChange={onRegimeChange}
          name="socialRegime"
        />
        <Select
          disabled={disabled}
          options={adjustmentOptions}
          icon={<MdOutlineCalculate size={22} />}
          label={T.OFFER.SIMULATOR.ADJUSTMENT.LABEL}
          value={commercialAdjustment}
          onChange={onCommercialAdjustmentChange}
          name="commercialAdjustment"
        />

        {costFailed && (
          <InfoBox
            variant="danger"
            icon={<MdWarningAmber size={24} />}
            title={T.OFFER.SIMULATOR.ERROR.TITLE}
            description={T.OFFER.SIMULATOR.ERROR.DESCRIPTION}
          />
        )}

        {!costFailed && costLoading && <Loading />}

        {!costFailed && !costLoading && isNil(costDetails) && (
          <InfoBox
            icon={<MdOutlineCalculate size={24} />}
            title={T.OFFER.SIMULATOR.EMPTY.TITLE}
            description={T.OFFER.SIMULATOR.EMPTY.DESCRIPTION}
          />
        )}

        {!costFailed && !isNil(costDetails) && (
          <>
            <div className="ac-collectif__pmss">
              {format(T.OFFER.SIMULATOR.VINTAGE, {
                vintage: get(costDetails, "vintage"),
              })}
            </div>

            {get(costDetails, "offers", []).map((offer) => {
              const combinationId = offer.combinationId || offer.rank;
              const offerSummary = get(summaries, combinationId, {});
              const upsells = get(offer, "upsells") || [];
              return (
                <div className="ac-collectif__offer-result" key={combinationId}>
                  <div className="ac-collectif__offer-result-head">
                    <h4>
                      {`${T.RANKS[offer.rank]} — ${format(T.LEVEL, {
                        level: offer.level,
                      })}`}
                    </h4>
                    <span className="ac-collectif__bracket">
                      {format(T.OFFER.SIMULATOR.RATED_BRACKET, {
                        bracket: offer.ratedBracket,
                      })}
                    </span>
                  </div>
                  <div className="ac-collectif__chips">
                    {isEmpty(upsells) ? (
                      <span className="ac-collectif__chip">
                        {T.OFFER.SIMULATOR.NO_REINFORCEMENT}
                      </span>
                    ) : (
                      upsells.map((upsell) => (
                        <span className="ac-collectif__chip" key={upsell.id}>
                          {upsell.name}
                        </span>
                      ))
                    )}
                  </div>

                  <div className="ac-collectif__table-scroll">
                    <table className="ac-collectif__table">
                      <thead>
                        <tr>
                          <th>{T.OFFER.SIMULATOR.TABLE.MEMBER}</th>
                          <th>{T.OFFER.SIMULATOR.TABLE.RATE}</th>
                          <th>{T.OFFER.SIMULATOR.TABLE.HEADCOUNT}</th>
                          <th>{T.OFFER.SIMULATOR.TABLE.ASSISTANCE}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {offer.lines.map((line) => (
                          <tr key={line.member}>
                            <td>{T.MEMBERS[line.member]}</td>
                            <td>
                              <span className="ac-collectif__rate">
                                <strong>
                                  {format(T.OFFER.SIMULATOR.TABLE.RATE_PMSS, {
                                    rate: formatRate(line.rate),
                                  })}
                                </strong>
                                <span>
                                  {format(T.OFFER.SIMULATOR.TABLE.RATE_AMOUNT, {
                                    amount: formatCurrency(
                                      line.monthlyTaxIncluded,
                                    ),
                                  })}
                                </span>
                              </span>
                            </td>
                            <td>
                              <Input
                                type="number"
                                min={0}
                                disabled={disabled}
                                name={`headcounts.${combinationId}.${line.member}`}
                                value={get(
                                  headcounts,
                                  [combinationId, line.member],
                                  0,
                                )}
                                onChange={({ target: { value } }) =>
                                  onHeadcountChange(
                                    combinationId,
                                    line.member,
                                    Number(value) || 0,
                                  )
                                }
                              />
                            </td>
                            <td>
                              {primaryMembers.includes(line.member)
                                ? format(
                                    T.OFFER.SIMULATOR.TABLE.ASSISTANCE_VALUE,
                                    {
                                      amount: formatCurrency(
                                        get(costDetails, "assistance"),
                                      ),
                                    },
                                  )
                                : NIL_PLACEHOLDER}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>{T.OFFER.SIMULATOR.TABLE.OFFER_TOTAL}</td>
                          <td>
                            {format(T.OFFER.SIMULATOR.TABLE.MONTHLY_VALUE, {
                              amount: formatCurrency(
                                get(offerSummary, "monthlyTaxIncluded"),
                              ),
                            })}
                          </td>
                          <td>{get(offerSummary, "insuredCount", 0)}</td>
                          <td>
                            {format(T.OFFER.SIMULATOR.TABLE.MONTHLY_VALUE, {
                              amount: formatCurrency(
                                get(offerSummary, "monthlyAssistance"),
                              ),
                            })}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              );
            })}

            {allocationErrors.length > 0 && (
              <InfoBox
                variant="danger"
                className="ac-collectif__composition-errors"
                icon={<MdWarningAmber size={24} />}
                title={T.OFFER.SIMULATOR.ALLOCATION.TITLE}
                description={
                  <ul>
                    {allocationErrors.map((error) => (
                      <li key={error.key}>
                        {compositionErrorMessage(T, error)}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
          </>
        )}
      </div>
    </Section>
  );
};

export default Simulator;
